import React from "react";
import { AlertTriangle, Code2, Minus, Pencil, Plus } from "lucide-react";

import { DETAIL_LIST } from "../constants";

// What a migration does, one line per step, read from the migration's own code.
//
// Grouped by whether the step creates, removes or changes something, because
// that is the only distinction that changes how carefully you read the list.
// A drop is the one that loses data.
const SHAPE = {
    add: { icon: Plus, tone: "text-tone-emerald" },
    remove: { icon: Minus, tone: "text-tone-rose" },
    change: { icon: Pencil, tone: "text-tone-amber" },
    raw: { icon: Code2, tone: "text-tone-violet" },
};

// Which shape each Alembic operation takes. Anything unlisted reads as a
// change, which is the honest default for an operation this build has no
// opinion about.
const SHAPE_OF = {
    create_table: "add",
    add_column: "add",
    create_index: "add",
    create_foreign_key: "add",
    create_unique_constraint: "add",
    create_check_constraint: "add",
    create_primary_key: "add",
    bulk_insert: "add",
    drop_table: "remove",
    drop_column: "remove",
    drop_index: "remove",
    drop_constraint: "remove",
    alter_column: "change",
    rename_table: "change",
    execute: "raw",
    get_bind: "raw",
};

/**
 * The fallback detail, shown for the 20 revisions whose SQL cannot be rendered.
 * Where the SQL is available, `RevisionSqlBlock` shows that instead.
 *
 * @param {Array} operations  the `upgradeOperations` / `downgradeOperations` array
 */
const OperationList = ({ operations }) => {
    if (!operations?.length) return null;

    return (
        <div className={DETAIL_LIST}>
            {shown.map((operation, index) => {
                const shape = SHAPE[SHAPE_OF[operation.kind] ?? "change"];
                const Icon = shape.icon;
                return (
                    <div
                        key={`${operation.kind}-${operation.table}-${operation.target}-${index}`}
                        className="px-3 py-1.5 flex items-start gap-2"
                    >
                        <Icon
                            size={11}
                            aria-hidden="true"
                            className={`${shape.tone} shrink-0 mt-1`}
                        />
                        <div className="min-w-0 flex-1">
                            {/* The summary already carries the table and column
                                names in backticks; rendering it as text keeps
                                them readable without a markdown pass. */}
                            <div className="text-[11px] text-text-secondary leading-snug break-words">
                                {operation.summary}
                            </div>
                        </div>
                    </div>
                );
            })}
            {hidden > 0 && (
                <div className="px-3 py-1.5 text-[11px] text-text-muted">
                    and {hidden} more
                </div>
            )}
        </div>
    );
};

/**
 * The one line above an operation list: what the migration does, and a warning
 * when part of it cannot be described.
 *
 * Raw SQL is called out rather than left implicit. A list of three operations
 * beside a migration that also runs an UPDATE reads as the whole story when it
 * is not, and the part that is missing is the part that touches data.
 */
export const OperationSummary = ({ revision }) => {
    if (!revision) return null;
    const rawSql = revision.upgradeRawSql ?? 0;
    return (
        <div className="space-y-1">
            {revision.operationSummary && (
                <div className="text-[11px] text-text-secondary">
                    {revision.operationSummary}
                </div>
            )}
            {rawSql > 0 && (
                <div className="text-[11px] text-tone-violet flex items-start gap-1">
                    <AlertTriangle size={11} className="shrink-0 mt-0.5" aria-hidden="true" />
                    <span>
                        {rawSql === 1
                            ? "One step is raw SQL"
                            : `${rawSql} steps are raw SQL`}
                        , so what it does to existing rows is not listed here.
                    </span>
                </div>
            )}
        </div>
    );
};

export default OperationList;
